/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PoslumaNavBar from '../../../micro/BrandNavBar/PoslumaNavBar';
import TrainingNavBar from '../../../micro/TrainingNavBar/PoslumaTrainingNavBar';
import { darkGrey, poslumaBlue, white, poslumaDarkBlue, poslumaTheme } from '../../../../app/theme';
import Indication from '../../../micro/Indication/PoslumaIndication';
import { BASE_ROUTE } from '../../../../constants/trainingNav.constants';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import history from '../../../../services/browserHistory';

const useStyles = makeStyles(() => ({
  gradientBox: {
    background: `transparent linear-gradient(180deg, ${white} 0%, ${white} 31%, ${poslumaBlue} 100%) 0% 0% no-repeat padding-box;`,
    width: '100%',
    margin: 0,
    padding: '0 40px',
    boxSizing: 'border-box',
  },
  header: {
    fontSize: 48,
    fontWeight: 'bold',
    textAlign: 'center',
    margin: '74px auto 40px',
  },
  subheader: {
    fontSize: 32,
    textAlign: 'center',
    marginBottom: 96,
  },
  recapWrapper: {
    width: '80%',
    margin: 'auto',
    paddingBottom: 78,
  },
  recapTitle: {
    fontSize: 32,
    paddingBottom: 10,
    width: '100%',
    borderBottom: `1px solid ${poslumaDarkBlue}`,
    marginBottom: 15,
  },
  recapRow: {
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  poslumaList: {
    listStyle: 'none',
    margin: 0,
    marginBottom: 30,
    marginTop: -5,
    padding: 0,
    fontSize: 20,
    lineHeight: 2.3,
    '& > li': {
      padding: '5px 45px',
      display: 'flex',
      flexDirection: 'row',
    },
    '& > li::before': {
      content: '"\u2022"',
      color: darkGrey,
      fontWeight: 600,
      paddingRight: 15,
    },
  },
  icon: {
    fontSize: 30,
    marginRight: 15,
  },
  recapText: {
    fontSize: 20,
    margin: 0,
  },
  grayBox: {
    backgroundColor: darkGrey,
    width: '100%',
    margin: 0,
    borderTop: `28px solid ${poslumaDarkBlue}`,
    padding: '55px 40px',
    boxSizing: 'border-box',
  },
  whiteTitle: {
    color: white,
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 28,
  },
  whiteBody: {
    color: white,
    margin: '60px 0 70px',
    textAlign: 'center',
    fontSize: 22,
  },
  whiteSubtitle: {
    maxWidth: '70%',
    margin: 'auto',
    color: white,
    fontSize: 28,
    textAlign: 'center',
    marginTop: 10,
  },
  link: {
    color: poslumaBlue,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
}));

const PoslumaTrainingEnd = (): JSX.Element => {
  const classes = useStyles();
  const [moduleDisplay, setModuleDisplay] = useState('');

  useEffect(() => {
    const pathName = history.location.pathname;
    if (pathName.indexOf('interpretation') >= 0) {
      setModuleDisplay('Interpretation');
      document.title = 'Image Interpretation Thank You';
    } else if (pathName.indexOf('acquisition') >= 0) {
      setModuleDisplay('Acquisition');
      document.title = 'Image Acquisition Thank You';
    }
  }, []);

  const handleClickEnter = () => {
    const baseRoute = BASE_ROUTE.poslumaInterpretation;
    history.push(`${baseRoute}`);
  };

  return (
    <MuiThemeProvider theme={poslumaTheme}>
      <TrainingNavBar />
      <PoslumaNavBar section={`Image ${moduleDisplay}`} />
      <div className={classes.gradientBox}>
        <Typography className={classes.header}>Thank you!</Typography>
        <Typography className={classes.subheader}>
          {`You have successfully completed the Image ${moduleDisplay} Training!`}
        </Typography>
        <div className={classes.recapWrapper}>
          <Typography className={classes.recapTitle}>Learning Objectives Recap</Typography>
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              Describe the biodistribution of&nbsp;
              {moduleDisplay === 'Acquisition' ? <span>POSLUMA&nbsp;</span> : 'POSLUMA '}
              and use the knowledge for PET image interpretation
            </p>
          </div>
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              Illustrate key technical aspects of the practical use of POSLUMA with respect to the following:
            </p>
          </div>
          <ul className={classes.poslumaList}>
            <li>Image instrumentation</li>
            <li>Patient preparation</li>
            <li>POSLUMA dosage and administration</li>
            <li>Image acquisition and reconstruction</li>
          </ul>
          {moduleDisplay === 'Interpretation' && (
            <>
              <div className={classes.recapRow}>
                <CheckCircleIcon className={classes.icon} />
                <p className={classes.recapText}>Describe the recommended image interpretation criteria for POSLUMA</p>
              </div>
              <div className={classes.recapRow}>
                <CheckCircleIcon className={classes.icon} />
                <p className={classes.recapText}>
                  Apply learnings from the recommended image interpretation training to patients who have undergone PET
                  scanning with POSLUMA
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={classes.grayBox}>
        <Typography className={classes.whiteTitle}>Other Avenues to Explore</Typography>
        <Typography className={classes.whiteSubtitle}>
          {`Additional cases are now unlocked within the training homepage.
          You will also be able to download an optional statement of completion.`}
        </Typography>
        <Typography className={classes.whiteSubtitle}>All areas of this site are now available to you.</Typography>
        {moduleDisplay === 'Acquisition' && (
          <>
            <Typography className={classes.whiteSubtitle}>
              Users who have completed the Acquisition training may navigate to the&nbsp;
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a onClick={handleClickEnter} className={classes.link}>
                Interpretation track
              </a>
              &nbsp;for additional training.
            </Typography>
          </>
        )}
      </div>
      <Indication />
      <TrainingFooter />
    </MuiThemeProvider>
  );
};

export default PoslumaTrainingEnd;
